import { useState, useCallback } from "react";
import { getBillsForVendor } from "../../../../services/vendorApi";
import { useDropdownCache } from "../../../../shared/presentation/hooks/useDropdownCache";
import { GetZohoBillDetailsUseCase } from "../../../../shared/domain/use-cases/GetZohoBillDetailsUseCase";
import { BillRepositoryImpl } from "../../../../shared/data/repositories/BillRepositoryImpl";

export interface VendorBillListItem {
  bill_id: string;
  vendor_id: string;
  vendor_name: string;
  bill_number?: string;
  date?: string;
  total: number;
  balance: number;
  tds_total: number;
  currency_code: string;
  zoho_billid?: string;
}

export interface UseVendorBillsReturn {
  bills: VendorBillListItem[];
  loading: boolean;
  error: string | null;
  fetchBills: (
    vendorZohoId: string,
    searchTerm: string,
    limit: number,
    offset: number
  ) => Promise<{
    items: VendorBillListItem[];
    hasMore?: boolean;
  }>;
  fetchBillDetails: (zohoBillId: string) => Promise<any>;
  clearError: () => void;
  reset: () => void;
}

// Create repository and use case instances (singleton pattern)
const billRepository = new BillRepositoryImpl();
const getZohoBillDetailsUseCase = new GetZohoBillDetailsUseCase(billRepository);

/**
 * Hook for fetching bills for a vendor with pagination and caching
 * Supports SearchableDropdown fetchItems function signature
 */
export const useVendorBills = (): UseVendorBillsReturn => {
  const [bills, setBills] = useState<VendorBillListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getCached, setCached } = useDropdownCache();

  /**
   * Fetch bills for a vendor with pagination and caching
   * Supports SearchableDropdown fetchItems function signature
   */
  const fetchBills = useCallback(
    async (
      vendorZohoId: string,
      searchTerm: string,
      limit: number,
      offset: number
    ): Promise<{
      items: VendorBillListItem[];
      hasMore?: boolean;
    }> => {
      if (!vendorZohoId || vendorZohoId.trim() === "") {
        return {
          items: [],
          hasMore: false,
        };
      }

      // Cache key includes Zoho vendor ID
      const cacheKey = { vendorZohoId, searchTerm, limit, offset };
      const shouldCache = offset === 0 && !searchTerm;

      if (shouldCache) {
        const cached = getCached<{
          items: VendorBillListItem[];
          hasMore?: boolean;
        }>("vendor_bills", cacheKey);

        if (cached) {
          return cached;
        }
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch bills for vendor
        const response = await getBillsForVendor(
          vendorZohoId.trim(),
          limit,
          offset,
          searchTerm || undefined
        );

        const items: VendorBillListItem[] = (response.data || []).map(
          (bill: any) => ({
            bill_id: bill.bill_id || "",
            vendor_id: bill.vendor_id || "",
            vendor_name: bill.vendor_name || "",
            bill_number: bill.bill_number || "",
            date: bill.date || "",
            total: bill.total || 0,
            balance: bill.balance || 0,
            tds_total: bill.tds_total || 0,
            currency_code: bill.currency_code || "",
            zoho_billid: bill.bill_id || "",
          })
        );

        const hasMore =
          (response as { pagination?: { has_more?: boolean } }).pagination
            ?.has_more ??
          (response as { has_more?: boolean }).has_more ??
          false;

        const result = {
          items,
          hasMore,
        };

        // Cache the result if it's the initial load
        if (shouldCache) {
          setCached("vendor_bills", cacheKey, result);
        }

        return result;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to fetch vendor bills";
        setError(errorMessage);
        console.error("Failed to fetch vendor bills:", error);
        return {
          items: [],
          hasMore: false,
        };
      } finally {
        setLoading(false);
      }
    },
    [getCached, setCached]
  );

  /**
   * Fetch bill details using GetZohoBillDetailsUseCase
   */
  const fetchBillDetails = useCallback(
    async (zohoBillId: string): Promise<any> => {
      setLoading(true);
      setError(null);

      try {
        const billDetails = await getZohoBillDetailsUseCase.execute(zohoBillId);
        return billDetails;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to fetch bill details";
        setError(errorMessage);
        console.error("Failed to fetch bill details:", error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * Reset state
   */
  const reset = useCallback((): void => {
    setBills([]);
    setError(null);
    setLoading(false);
  }, []);

  return {
    bills,
    loading,
    error,
    fetchBills,
    fetchBillDetails,
    clearError,
    reset,
  };
};
